import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  MapPin,
  BarChart3,
  Scale,
  TrendingUp,
  Layers,
  Building2,
  Eye,
  Coins,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { useDocumentSEO } from "@/hooks/useDocumentSEO";

const HERO_IMAGE =
  "https://images.pexels.com/photos/374870/pexels-photo-374870.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=1600";

const MID_IMAGE =
  "https://images.pexels.com/photos/302769/pexels-photo-302769.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

const YAKLASIM = [
  {
    icon: MapPin,
    n: "01",
    title: "Arsa ve Proje Potansiyeli Değerlendirmesi",
    body:
      "Konum, imar durumu, çevresel bağlam ve olası kullanım senaryolarını birlikte inceleriz. Her arsanın kendi doğasına özgü potansiyelini objektif biçimde değerlendirir; erken aşamada gerçekçi bir çerçeve ortaya koyarız.",
  },
  {
    icon: BarChart3,
    n: "02",
    title: "Pazar ve Talep Analizi",
    body:
      "Hedef kullanıcı profilini, arz–talep dengesini ve rekabet ortamını çalışırız. Amacımız; projeyi piyasadaki gerçek ihtiyaçla uyumlu ve sürdürülebilir bir konuma yerleştirmektir.",
  },
  {
    icon: Scale,
    n: "03",
    title: "Mimari ve Ticari Fizibilite Dengesi",
    body:
      "Fizibiliteyi salt maliyet–gelir denklemi olarak değil; mimari kalite, işletme ömrü ve marka değeriyle birlikte kurarız. Kararlar bu dengeyi bozmayacak biçimde şekillenir.",
  },
  {
    icon: TrendingUp,
    n: "04",
    title: "Uzun Vadeli Değer Yönetimi",
    body:
      "Bir gayrimenkulün değerinin yalnızca teslimatta değil, sonraki yıllar boyunca da korunmasını gözetiriz. Malzeme, standart ve süreç kararlarımızın merkezinde bu yaklaşım yer alır.",
  },
];

const NEDENLER = [
  {
    icon: Layers,
    title: "Mimari ve Ticari Bakışın Bir Arada Değerlendirilmesi",
    body:
      "Tasarım kararlarını yatırım kararlarıyla, yatırım kararlarını da tasarım disipliniyle birlikte alırız.",
  },
  {
    icon: Building2,
    title: "1001 Grup'un Kurumsal Yatırım Disiplini",
    body:
      "Değerlendirmelerimizde; grubun köklü ticari birikim ve yatırım anlayışı bir zemin oluşturur.",
  },
  {
    icon: Eye,
    title: "Şeffaf ve Planlı Geliştirme Süreci",
    body:
      "Süreç izlenebilir raporlamayla yürür; karar noktaları önceden tanımlıdır.",
  },
  {
    icon: Coins,
    title: "Uzun Vadeli Değer Odaklı Yaklaşım",
    body:
      "Kısa vadeli maksimizasyon yerine, kalıcı değer yaratımını önceliklendiririz.",
  },
];

export default function GayrimenkulPage() {
  useDocumentSEO({
    title: "Gayrimenkul Geliştirme",
    description:
      "Mimari bakışı yatırım perspektifiyle birleştiren, arsa değerlendirmesinden proje geliştirmeye uzanan yatırım odaklı gayrimenkul geliştirme yaklaşımı.",
  });
  return (
    <>
      <PageHeader
        breadcrumb="Gayrimenkul Geliştirme"
        overline="Değer Üretimi"
        title="Gayrimenkul Geliştirme"
        subtitle="Mimari bakış açısını yatırım perspektifiyle birleştiren, uzun vadeli değer odaklı bir geliştirme yaklaşımı."
      />

      {/* 1. GİRİŞ + HERO GÖRSEL */}
      <section
        data-testid="gayrimenkul-intro"
        className="bg-white py-24 lg:py-32"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-[1px] bg-gold" />
              <span className="overline text-anthracite/70">Giriş</span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-navy leading-tight">
              Mimari bakışın
              <br />
              <span className="text-gold italic font-extralight">
                yatırım perspektifiyle
              </span>
              <br />
              buluştuğu alan.
            </h2>

            <div className="mt-8 space-y-5 text-base md:text-[17px] leading-relaxed text-anthracite/85 font-light max-w-xl">
              <p>
                1001 Mimarlık İnşaat için gayrimenkul geliştirme; mimari bakış
                açısının ticari perspektifle bir araya geldiği bütüncül bir
                alandır. Doğru arsa ve proje değerlendirmesinden başlayarak;
                planlı, izlenebilir ve uzun vadeli değer üretimine odaklanmış
                bir geliştirme sürecini benimseriz.
              </p>
              <p>
                Amacımız; projenin yalnızca kısa vadeli çıktısıyla değil,
                içinde bulunduğu bağlamda uzun soluklu değer taşımasıyla
                değerlendirilmesidir. Bu nedenle her geliştirme kararını
                mimari, işlevsel, hukuki ve ticari bileşenlerin birlikte
                tartıldığı bir çerçevede alırız.
              </p>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-[1px] bg-navy/10 max-w-md">
              {[
                { k: "Bakış", v: "Mimari" },
                { k: "Perspektif", v: "Yatırım" },
                { k: "Odak", v: "Uzun Vade" },
              ].map((item) => (
                <div key={item.k} className="bg-white p-4 border border-navy/5">
                  <div className="overline text-anthracite/60 text-[9.5px]">
                    {item.k}
                  </div>
                  <div className="mt-2 font-heading text-[14px] text-navy">
                    {item.v}
                  </div>
                  <div className="mt-3 w-6 h-[1px] bg-gold" />
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-6">
            <div className="relative overflow-hidden">
              <img
                src={HERO_IMAGE}
                alt="Şehir silüeti"
                className="w-full aspect-[4/5] md:aspect-[5/6] object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-navy/30 mix-blend-multiply" />
              <div className="absolute inset-0 border border-gold/40" />
              <div className="absolute top-4 left-4 w-6 h-[1px] bg-gold" />
              <div className="absolute top-4 left-4 w-[1px] h-6 bg-gold" />
              <div className="absolute bottom-4 right-4 w-6 h-[1px] bg-gold" />
              <div className="absolute bottom-4 right-4 w-[1px] h-6 bg-gold" />

              {/* Overlay label */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="overline text-white/70 mb-1">Alan</div>
                <div className="font-heading text-white text-lg font-light">
                  Gayrimenkul geliştirmede kurumsal disiplin
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. YAKLAŞIMIMIZ */}
      <section
        data-testid="gayrimenkul-yaklasim"
        className="bg-anthracite text-white py-24 lg:py-32 relative overflow-hidden"
      >
        <div className="absolute inset-0 blueprint-grid opacity-25 pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 items-end">
            <div className="md:col-span-5">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-[1px] bg-gold" />
                <span className="overline text-gold">Yaklaşımımız</span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light tracking-tight leading-tight">
                Yaklaşımımız.
              </h2>
              <div className="mt-6 w-16 h-[1px] bg-gold/60" />
            </div>
            <div className="md:col-span-6 md:col-start-7">
              <p className="text-base md:text-lg leading-relaxed text-white/75 font-light">
                Gayrimenkul geliştirme sürecini birbirini tamamlayan dört ana
                bakış açısı üzerine kurarız. Bu bakış açılarını birbirinden
                kopuk değil, aynı masada değerlendirilen bileşenler olarak ele
                alırız.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-[1px] bg-white/10">
            {YAKLASIM.map(({ icon: Icon, n, title, body }) => (
              <div
                key={n}
                data-testid={`gayrimenkul-yaklasim-${n}`}
                className="group bg-anthracite hover:bg-navy transition-colors p-8 lg:p-12 flex flex-col min-h-[280px]"
              >
                <div className="flex items-start justify-between">
                  <span className="w-12 h-12 border border-gold/50 flex items-center justify-center text-gold shrink-0 group-hover:bg-gold group-hover:text-white transition-colors">
                    <Icon size={20} strokeWidth={1.5} />
                  </span>
                  <span className="w-8 h-[1px] bg-gold/60 mt-6" />
                </div>
                <h3 className="mt-8 font-heading text-xl md:text-2xl font-medium leading-snug">
                  {title}
                </h3>
                <div className="mt-4 w-10 h-[1px] bg-gold/60" />
                <p className="mt-4 text-[14.5px] leading-relaxed text-white/75 font-light">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. GÖRSEL BAĞLAM BANDI */}
      <section
        data-testid="gayrimenkul-visual-band"
        className="bg-white"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 items-stretch">
          <div className="md:col-span-6">
            <img
              src={MID_IMAGE}
              alt="Kentsel bağlam / imar"
              className="w-full h-[320px] md:h-[520px] object-cover"
              loading="lazy"
            />
          </div>
          <div className="md:col-span-6 bg-navy text-white p-10 lg:p-16 flex flex-col justify-center relative overflow-hidden">
            <div className="absolute inset-0 blueprint-grid opacity-25 pointer-events-none" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-[1px] bg-gold" />
                <span className="overline text-gold">Perspektif</span>
              </div>
              <blockquote className="font-heading text-2xl md:text-3xl lg:text-4xl font-light leading-[1.25] tracking-tight text-white/95">
                “Bir gayrimenkul geliştirme kararı;
                <br className="hidden md:block" />
                aynı zamanda o bölgenin geleceğine
                <br className="hidden md:block" />
                verilmiş bir cevaptır.”
              </blockquote>
              <div className="mt-8 flex items-center gap-3 text-white/60 text-[11px] tracking-[0.3em] uppercase">
                <span className="w-8 h-[1px] bg-gold" />
                1001 Mimarlık İnşaat
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. NEDEN 1001 (Gayrimenkulde) */}
      <section
        data-testid="gayrimenkul-neden"
        className="bg-white py-24 lg:py-32 relative overflow-hidden"
      >
        <div className="absolute inset-0 blueprint-grid opacity-40 pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14 items-end">
            <div className="md:col-span-6">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-[1px] bg-gold" />
                <span className="overline text-anthracite/70">Neden 1001</span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-navy leading-tight">
                Gayrimenkul Geliştirmede
                <br />
                <span className="text-gold italic font-extralight">
                  Neden 1001
                </span>{" "}
                Mimarlık İnşaat?
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8">
              <p className="text-base leading-relaxed text-anthracite/80 font-light">
                Gayrimenkul geliştirmede güven; sayılarla değil, kararların
                nasıl alındığı ve sürecin nasıl işletildiğiyle kurulur.
                Farkımızı burada aramanızı öneriyoruz.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-[1px] bg-navy/10">
            {NEDENLER.map(({ icon: Icon, title, body }, i) => (
              <div
                key={title}
                data-testid={`gayrimenkul-neden-${i}`}
                className="group bg-white p-8 lg:p-10 flex flex-col min-h-[260px] border border-navy/5 hover:bg-navy hover:text-white transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="w-11 h-11 border border-navy/20 group-hover:border-gold/60 flex items-center justify-center text-gold shrink-0 transition-colors">
                    <Icon size={18} strokeWidth={1.5} />
                  </span>
                  <span className="font-heading text-[12px] tracking-[0.3em] text-anthracite/40 group-hover:text-gold/70">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="mt-8 font-heading text-lg font-medium leading-snug">
                  {title}
                </h3>
                <p className="mt-4 text-[13.5px] leading-relaxed opacity-80 font-light">
                  {body}
                </p>
                <div className="mt-6 w-8 h-[1px] bg-gold group-hover:w-12 transition-all" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. ALT CTA */}
      <section
        data-testid="gayrimenkul-cta"
        className="bg-navy text-white py-24 lg:py-28 relative overflow-hidden"
      >
        <div className="absolute inset-0 blueprint-grid opacity-30 pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="overline text-gold mb-5">İletişim</div>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light tracking-tight leading-tight">
              Gayrimenkul geliştirme
              <br />
              projeniz için{" "}
              <span className="text-gold italic font-extralight">görüşelim.</span>
            </h2>
            <p className="mt-6 max-w-xl text-white/70 leading-relaxed font-light">
              Fikir aşamasındaki bir arsa değerlendirmesi, fizibilite çalışması
              veya birlikte geliştireceğimiz bir proje için ekibimizle
              iletişime geçebilirsiniz.
            </p>
          </div>
          <div className="md:col-span-4 md:flex md:justify-end">
            <Link
              to="/iletisim"
              data-testid="gayrimenkul-cta-iletisim"
              className="group inline-flex items-center gap-3 bg-gold hover:bg-gold-hover text-white px-8 py-4 text-[12.5px] tracking-widest uppercase font-medium transition-colors"
            >
              Bizimle İletişime Geçin
              <ArrowRight
                size={16}
                className="transition-transform group-hover:translate-x-1"
              />
            </Link>
          </div>
        </div>
      </section>

      {/* Alt küçük geçiş */}
      <section className="bg-white py-10 border-t border-navy/5">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="text-[12px] tracking-[0.25em] uppercase text-anthracite/60">
            Devamı
          </div>
          <Link
            to="/proje-yaklasimimiz"
            data-testid="gayrimenkul-next-yaklasim"
            className="group inline-flex items-center gap-3 text-navy hover:text-gold transition-colors text-[13px] tracking-widest uppercase font-medium"
          >
            Proje Yaklaşımımız
            <ArrowUpRight
              size={15}
              className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
            />
          </Link>
        </div>
      </section>
    </>
  );
}
